import type { CoastalWetlandsResponse, ModuleFull } from "@features/modules/coastalWetlands/types";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { FEModules, FEModulesObj } from "../../../utils/moduleList";
import { prepareModulePayload, transformModuleResponse } from "./factories/moduleApiUtils";
import { createModuleGetApi, createModuleUpdateApi } from "./factories/createModuleApi";

const includedPropList = [
	"id",
	"status",
	"note",
	"ha_start",
	"ha_w",
	"ha_wo",
	"land_use_type",
	"ha_thread",
	"area",
	"area_under_drainage_start",
	"area_under_drainage_w",
	"area_under_drainage_wo",
	"area_under_drainage_thread",
	"drained_area_excavated_start",
	"drained_area_excavated_w",
	"drained_area_excavated_wo",
	"drained_area_excavated_thread",
	"area_w_restored_vegetation_start",
	"area_w_restored_vegetation_w",
	"area_w_restored_vegetation_wo",
	"area_w_restored_vegetation_thread",
];
const includedT2PropList = [
	"soc_t2_start",
	"soc_t2_w",
	"soc_t2_wo",
	"pc_c_lost_after_excavation_t2_start",
	"pc_c_lost_after_excavation_t2_w",
	"pc_c_lost_after_excavation_t2_wo",
	"agb_t2_start",
	"agb_t2_w",
	"agb_t2_wo",
	"bgb_t2_start",
	"bgb_t2_w",
	"bgb_t2_wo",
	"litter_t2_start",
	"litter_t2_w",
	"litter_t2_wo",
	"deadwood_t2_start",
	"deadwood_t2_w",
	"deadwood_t2_wo",
	"drainage_ef_t2_start",
	"drainage_ef_t2_w",
	"drainage_ef_t2_wo",
	"co2_rewetting_t2_start",
	"co2_rewetting_t2_w",
	"co2_rewetting_t2_wo",
	"ch4_rewetting_t2_start",
	"ch4_rewetting_t2_w",
	"ch4_rewetting_t2_wo",
	"soil_type_t2",
	"avg_salinity_t2",
];

// Define constants for the module
const MODULE_TYPE = "CoastalWetlands";
const TAG: TagTypes = "CoastalWetlands";
const TAG_DEFAULTS: TagTypes = "CoastalWetlandsDefaults";
const ENDPOINT = FEModulesObj[FEModules.CoastalWetlands].path;

const t2PercentageProps = ["pc_c_lost_after_excavation_t2"];
const transformResponse = (response: CoastalWetlandsResponse) => transformModuleResponse<CoastalWetlandsResponse, ModuleFull>({
  response,
  includedPropList,
  includedT2PropList,
  t2PercentageProps,
})

const coastalWetlandsApiSlice = createModuleGetApi<ModuleFull>({
  apiSlice,
  path: ENDPOINT,
  moduleName: MODULE_TYPE,
  tag: TAG,
  tagDefaults: TAG_DEFAULTS,
  transformResponse,
})

const publicCoastalWetlandsApiSlice = createModuleGetApi<ModuleFull>({
  apiSlice: publicApiSlice,
  path: ENDPOINT,
  moduleName: MODULE_TYPE,
  tag: TAG,
  tagDefaults: TAG_DEFAULTS,
  transformResponse,
})

export const {
  useGetCoastalWetlandsQuery,
  useGetCoastalWetlandsDefaultsQuery,
} = coastalWetlandsApiSlice;

export const {
  useGetCoastalWetlandsQuery: usePublicGetCoastalWetlandsQuery,
  useGetCoastalWetlandsDefaultsQuery: usePublicGetCoastalWetlandsDefaultsQuery,
} = publicCoastalWetlandsApiSlice;


const transformUpdatePayload = (data: Partial<ModuleFull>, activityId: number) => prepareModulePayload({
  moduleData: data.module,
  tiertwoData: data.tiertwo,
  t2PercentageProps,
  activityId
})

const mutationApiSlice = createModuleUpdateApi<ModuleFull, CoastalWetlandsResponse>({
  apiSlice,
  path: ENDPOINT,
  moduleName: MODULE_TYPE,
  tag: TAG,
  tagDefaults: TAG_DEFAULTS,
  transformUpdatePayload,
})

export const {
  useUpdateCoastalWetlandsMutation,
} = mutationApiSlice;

export const coastalWetlandsEndpoints = coastalWetlandsApiSlice.endpoints;
export const coastalWetlandsMutationEndpoints = mutationApiSlice.endpoints;